import React, { useState, useEffect } from 'react';
import usePopupClose from '../Utils/usePopupClose';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import toast from 'react-hot-toast';

const PublicLinks = ({ isOpen, onClose, entity, linkPrefix }) => {
  const modalRef = usePopupClose(isOpen, onClose);
  const { user } = useSession();
  
  // State for loading and links
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [links, setLinks] = useState([]);
  const [editingField, setEditingField] = useState(null); // { linkId, field }
  const [editingValue, setEditingValue] = useState('');

  const handleCopyLink = (link) => {
    const fullUrl = `${window.location.origin}${link}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      toast.success('Link copied to clipboard!');
      console.log('Link copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy link: ', err);
      toast.error('Failed to copy link to clipboard');
    });
  };

  const handleToggleActive = async (linkId) => {
    const link = links.find(l => l.link_id === linkId);
    if (!link) return;

    const newActiveStatus = !link.link_active;
    
    // Optimistically update UI
    setLinks(prevLinks => 
      prevLinks.map(l => 
        l.link_id === linkId ? { ...l, link_active: newActiveStatus } : l
      )
    );

    // Send update to server
    await handleUpdateLink(linkId, { link_active: newActiveStatus });
  };

  const handleUpdateLink = async (linkId, updates) => {
    try {
      const requestBody = {
        action: 'update',
        link_id: linkId,
        ...updates
      };

      const response = await fetch(API_ENDPOINTS.MANAGE_LINKS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      toast.success('Link updated successfully!');
      
    } catch (error) {
      console.error('Error updating link:', error);
      
      if (error.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      toast.error('Error updating link. Please try again.');
      
      // Reload links to revert optimistic update
      await loadLinks();
    }
  };

  const startEditing = (linkId, field, currentValue) => {
    setEditingField({ linkId, field });
    setEditingValue(currentValue);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue('');
  };

  const saveEdit = async () => {
    if (!editingField || !editingValue.trim()) {
      cancelEditing();
      return;
    }

    const { linkId, field } = editingField;
    const updates = { [field]: editingValue.trim() };

    // If editing expiry_date, convert to ISO format
    if (field === 'expiry_date') {
      try {
        const date = new Date(editingValue);
        updates[field] = date.toISOString();
      } catch (error) {
        toast.error('Invalid date format');
        return;
      }
    }

    // Optimistically update UI
    setLinks(prevLinks => 
      prevLinks.map(link => 
        link.link_id === linkId ? { ...link, ...updates } : link
      )
    );

    cancelEditing();
    await handleUpdateLink(linkId, updates);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    try {
      const requestBody = {
        action: 'delete',
        link_id: linkId
      };

      const response = await fetch(API_ENDPOINTS.MANAGE_LINKS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      toast.success('Link deleted successfully!');
      
      // Reload the list of links from server
      await loadLinks();
      
    } catch (error) {
      console.error('Error deleting link:', error);
      
      if (error.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      toast.error('Error deleting link. Please try again.');
    }
  };

  // Load links from server
  const loadLinks = async () => {
    if (!entity?.entity_id || !user?.access_token) {
      return;
    }

    setIsLoadingLinks(true);
    
    try {
      const requestBody = {
        action: 'get',
        entity_id: entity.entity_id
      };

      const response = await fetch(API_ENDPOINTS.MANAGE_LINKS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      console.log('Links loaded successfully:', result);
      
      // Set the links from server response (always an array)
      setLinks(result);
      
    } catch (error) {
      console.error('Error loading links:', error);
      
      // If it's a 401 error, it's an expired session
      if (error.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      // Don't show error toast for loading links as it might be normal to have no links
      console.log('Could not load links, starting with empty list');
      setLinks([]);
    } finally {
      setIsLoadingLinks(false);
    }
  };

  // Load links when modal opens
  useEffect(() => {
    if (isOpen && entity?.entity_id && user?.access_token) {
      loadLinks();
    }
  }, [isOpen, entity?.entity_id, user?.access_token]);

  // Generate UUID function
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleGenerateNewLink = async () => {
    if (!entity?.entity_id) {
      toast.error('No entity ID available. Cannot generate link.');
      return;
    }

    if (!entity?.entity_type) {
      toast.error('No entity type available. Cannot generate link.');
      return;
    }

    if (!user?.access_token) {
      toast.error('Authentication required. Please log in again.');
      return;
    }

    setIsGeneratingLink(true);
    
    try {
      // Generate UUID for the password
      const uuid = generateUUID();
      
      // Calculate expiry date (30 days from now)
      const expiryDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      
      // Format current timestamp for the name (full timestamp to distinguish between multiple links created same day)
      const currentTimestamp = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      // Generate generic name based on entity type
      const entityTypeName = entity.entity_type.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');

      const requestBody = {
        action: 'generate',
        entity_type: entity.entity_type,
        entity_id: entity.entity_id,
        name: `${entityTypeName} ${currentTimestamp}`,
        expiry_date: expiryDate,
        link_active: true,
        password: uuid
      };

      const response = await fetch(API_ENDPOINTS.MANAGE_LINKS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      console.log('Link generation successful:', result);
      toast.success('Public link generated successfully!');
      
      // Reload the list of links from server
      await loadLinks();
      
    } catch (error) {
      console.error('Error generating link:', error);
      
      // If it's a 401 error, it's an expired session
      if (error.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      toast.error('Error generating link. Please try again.');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] overflow-visible">
      {/* Backdrop - dark backdrop similar to CreateEvaluation */}
      <div className="fixed inset-0 bg-black bg-opacity-50"></div>
      
      {/* Modal */}
      <div ref={modalRef} className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
        {/* Close button - Top right */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Modal content - No header */}
        <div className="flex-1 overflow-hidden">
          <div className="p-6 h-full overflow-y-auto">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Public Links</h2>
              <p className="text-gray-600 mt-1">Manage your public document links</p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleGenerateNewLink}
              disabled={isGeneratingLink}
              className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center ${
                isGeneratingLink
                  ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
              }`}
            >
              {isGeneratingLink ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                'Generate New Link'
              )}
            </button>
          </div>

          {/* Loading State */}
          {isLoadingLinks ? (
            <div className="text-center py-12">
              <svg className="animate-spin mx-auto h-12 w-12 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Loading Links...</h3>
              <p className="mt-1 text-sm text-gray-500">Please wait while we fetch your public links.</p>
            </div>
          ) : (
            <>
              {/* Links Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Link
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expiry Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {links.map((link) => (
                      <tr key={link.link_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-base font-medium text-gray-900">
                            {editingField?.linkId === link.link_id && editingField?.field === 'name' ? (
                              <input
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={handleKeyPress}
                                onBlur={saveEdit}
                                className="w-full px-2 py-1 text-base border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                            ) : (
                              <span
                                onClick={() => startEditing(link.link_id, 'name', link.name)}
                                className="cursor-pointer hover:bg-gray-100 px-1 py-0.5 rounded"
                                title="Click to edit"
                              >
                                {link.name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div 
                              onClick={() => window.open(`${window.location.origin}${linkPrefix}${link.entity_id}/${link.password}`, '_blank')}
                              className="text-sm text-blue-600 hover:text-blue-800 font-mono bg-gray-100 px-2 py-1 rounded max-w-xs truncate cursor-pointer hover:bg-gray-200 transition-colors"
                              title="Click to open in new tab"
                            >
                              {window.location.origin}{linkPrefix}{link.entity_id}/{link.password}
                            </div>
                            <button
                              onClick={() => handleCopyLink(`${linkPrefix}${link.entity_id}/${link.password}`)}
                              className="inline-flex items-center p-1 border border-transparent rounded text-xs font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              title="Copy link"
                            >
                              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {editingField?.linkId === link.link_id && editingField?.field === 'expiry_date' ? (
                            <input
                              type="date"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={handleKeyPress}
                              onBlur={saveEdit}
                              className="px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                          ) : (
                            <span
                              onClick={() => startEditing(link.link_id, 'expiry_date', link.expiry_date.split('T')[0])}
                              className="cursor-pointer hover:bg-gray-100 px-1 py-0.5 rounded"
                              title="Click to edit"
                            >
                              {formatDate(link.expiry_date)}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleActive(link.link_id)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                              link.link_active ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                            role="switch"
                            aria-checked={link.link_active}
                          >
                            <span
                              aria-hidden="true"
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                link.link_active ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                          <span className={`ml-2 text-sm ${link.link_active ? 'text-green-600' : 'text-gray-500'}`}>
                            {link.link_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleDeleteLink(link.link_id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Empty State */}
              {links.length === 0 && (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A10.003 10.003 0 0124 26c4.21 0 7.813 2.602 9.288 6.286M30 14a6 6 0 11-12 0 6 6 0 0112 0zm12 6a4 4 0 11-8 0 4 4 0 018 0zm-28 0a4 4 0 11-8 0 4 4 0 018 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No public links</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by generating your first public link.</p>
                  <div className="mt-6">
                    <button
                      onClick={handleGenerateNewLink}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Generate New Link
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </div>

        {/* Footer with Close Button */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicLinks;
